
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wifi, WifiOff, Globe, Smartphone } from "lucide-react";

export const ConnectionDebugger = () => {
  const [connectionInfo, setConnectionInfo] = useState({
    online: navigator.onLine,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    origin: window.location.origin,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    port: window.location.port,
    href: window.location.href,
    networkType: 'unknown'
  });

  useEffect(() => {
    const updateConnectionInfo = () => {
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      
      setConnectionInfo({
        online: navigator.onLine,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        origin: window.location.origin,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        port: window.location.port,
        href: window.location.href,
        networkType: connection ? connection.effectiveType || connection.type : 'unknown'
      });
    };

    updateConnectionInfo();

    const handleOnline = () => updateConnectionInfo();
    const handleOffline = () => updateConnectionInfo();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(connectionInfo.userAgent);
  const isLocalhost = connectionInfo.hostname === 'localhost' || connectionInfo.hostname === '127.0.0.1';

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {connectionInfo.online ? (
            <Wifi className="h-5 w-5 text-green-600" />
          ) : (
            <WifiOff className="h-5 w-5 text-red-600" />
          )}
          Connection Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant={connectionInfo.online ? "default" : "destructive"}>
            {connectionInfo.online ? "Online" : "Offline"}
          </Badge>
          {isMobile && (
            <Badge variant="outline">
              <Smartphone className="h-3 w-3 mr-1" />
              Mobile
            </Badge>
          )}
          {isLocalhost && (
            <Badge variant="destructive">
              <Globe className="h-3 w-3 mr-1" />
              Localhost
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div>
            <strong>Origin:</strong> {connectionInfo.origin}
          </div>
          <div>
            <strong>Hostname:</strong> {connectionInfo.hostname}
          </div>
          <div>
            <strong>Protocol:</strong> {connectionInfo.protocol}
          </div>
          <div>
            <strong>Port:</strong> {connectionInfo.port || 'default'}
          </div>
          <div>
            <strong>Platform:</strong> {connectionInfo.platform}
          </div>
          <div>
            <strong>Network:</strong> {connectionInfo.networkType}
          </div>
        </div>

        <div className="text-xs text-gray-600 break-all">
          <strong>Full URL:</strong> {connectionInfo.href}
        </div>

        {isLocalhost && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            <strong>⚠️ Localhost Detected:</strong> This app is running on localhost, which is only accessible from this device. 
            Published apps should not show localhost URLs.
          </div>
        )}

        <div className="text-xs text-gray-500">
          User Agent: {connectionInfo.userAgent}
        </div>
      </CardContent>
    </Card>
  );
};
